import { createContext, useContext } from 'react'

/**
 * The browser fires `beforeinstallprompt` once the PWA install criteria are met
 * (manifest + service worker + engagement). It isn't part of the DOM lib types,
 * so we model just the members we use.
 */
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
  prompt: () => Promise<void>
}

export interface PwaInstallValue {
  /** A deferred install prompt is available to present. */
  canInstall: boolean
  /**
   * The app is already installed — either it's running as an installed PWA
   * (standalone display mode) or `appinstalled` fired during this session.
   */
  installed: boolean
  /** The user closed the install banner during this browser session. */
  dismissed: boolean
  /** Hide install affordances for the rest of this browser session. */
  dismiss: () => void
  /** Present the deferred install prompt (no-op when none is available). */
  promptInstall: () => Promise<void>
}

export const PwaInstallContext = createContext<PwaInstallValue | null>(null)

/**
 * Session-scoped, so a dismissal survives in-session navigation/reloads but the
 * banner returns on the user's next visit. sessionStorage (not localStorage) is
 * the whole point: dismissing is "not right now", not "never again".
 */
export const PWA_DISMISSED_KEY = 'edf.pwa-install.dismissed'

/**
 * True when the page is running as an installed app rather than in a browser
 * tab. `display-mode: standalone` covers Android/desktop installs; the
 * non-standard `navigator.standalone` covers iOS Safari's Add to Home Screen,
 * which never fires `beforeinstallprompt` at all.
 */
export function isRunningStandalone() {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

/**
 * Read install availability + the prompt trigger. The browser's automatic
 * prompt is suppressed app-wide by <PwaInstallProvider>; each surface decides
 * WHERE to offer install by rendering an affordance (e.g. <InstallPrompt/>) —
 * which is intentionally absent from the pre-qualification flow, so a prompt can
 * only be offered from the login screen onward.
 */
export function usePwaInstall() {
  const context = useContext(PwaInstallContext)
  if (!context) {
    throw new Error('usePwaInstall must be used within <PwaInstallProvider>')
  }
  return context
}
